/**
 * 送付状の束ね（#223 の2回目、docs/89 §6）。
 *   CoverBundlePicker：「書類追加」の窓。タブ 帳票／受け取った書類／PDF ファイル
 *     ・帳票：同じ送付先（{customer}・支店・送信先 FAX が同じ）の出した帳票と見積依頼書（{project}をまたぐ。POST /cover-letters/candidates）
 *     ・受け取った書類：{project}を選び、その{project}の受け取った書類（PDF・画像。画像は A4 縦の1ページに収める）
 *     ・PDF ファイル：その場で選ぶ PDF（POST /cover-letters/uploads。送付の記録にだけ残る＝{project}の受け取った書類には入らない）
 *     もう入っている書類は「同封済」で選べない。決めると onPick（選んだ順）
 *   useItemPages：同封書類1つずつの PDF（束ねで入るのと同じバイト＝POST /cover-letters/item-pdf）を紙の画像にして並べる
 *   PC だけ（contract の PC_ONLY_FEATURES。帳票の紙の画面の枠もスマホには出さない）
 *   fileOnly＝未登録の送付先（3回目の前の直し＝★13）：タブは「PDF ファイル」だけ（帳票・受け取った書類の候補は読まない）
 */
import {
  COVER_BUNDLE_LIMITS,
  COVER_LETTER_MSG as M,
  coverItemKey,
  encodeTitleHeader,
  type CoverCandidates,
  type CoverUpload,
  type DeliveryItemRef,
  type DeliveryTargetInput,
} from "@genbaryoku/contract";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, apiUpload, fetchFile, messageOf } from "../api.js";
import { t } from "../labels.js";
import { renderPdf, type PageImg } from "../lib/pdfPages.js";
import { FileDrop } from "./FileDrop.jsx";
import { Segmented } from "./Segmented.jsx";
import { ErrorText, Help } from "./ui.jsx";

type Tab = "docs" | "received" | "file";

export function CoverBundlePicker({
  target,
  onClose,
  onPick,
  fileOnly = false,
}: {
  target: DeliveryTargetInput;
  onClose: () => void;
  onPick: (refs: DeliveryItemRef[], labels: Record<string, string>) => void;
  fileOnly?: boolean;
}) {
  const [tab, setTab] = useState<Tab>(fileOnly ? "file" : "docs");
  const [groupId, setGroupId] = useState<string | null>(null);
  const cand = useQuery({
    queryKey: ["cover-candidates", JSON.stringify(target), groupId],
    queryFn: () => api<CoverCandidates>("POST", "/cover-letters/candidates", { target, groupId }),
    enabled: !fileOnly,
    retry: false,
  });
  /** 選んだもの（選んだ順） */
  const [picked, setPicked] = useState<DeliveryItemRef[]>([]);
  const [uploads, setUploads] = useState<CoverUpload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  const has = (k: string) => picked.some((r) => coverItemKey(r) === k);
  const toggle = (r: DeliveryItemRef) => setPicked((ps) => (ps.some((x) => coverItemKey(x) === coverItemKey(r)) ? ps.filter((x) => coverItemKey(x) !== coverItemKey(r)) : [...ps, r]));
  const room = COVER_BUNDLE_LIMITS.items - (target.items?.length ?? 0);
  const full = picked.length >= room;
  const upload = async (files: File[]) => {
    setErr(undefined);
    setUploading(true);
    try {
      for (const f of files) {
        if (picked.length + 1 > room) throw new Error(M.bundleTooMany.replace("{n}", String(COVER_BUNDLE_LIMITS.items)));
        if (f.size > COVER_BUNDLE_LIMITS.bytes) throw new Error(M.fileTooBig);
        const u = await apiUpload<CoverUpload>("/cover-letters/uploads", f, "application/pdf", { "x-gb-title": encodeTitleHeader(f.name) });
        setUploads((us) => [...us, u]);
        setPicked((ps) => [...ps, { kind: "file", id: u.id }]);
      }
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setUploading(false);
    }
  };
  const done = () => {
    const labels: Record<string, string> = {};
    for (const u of uploads) labels[coverItemKey({ kind: "file", id: u.id })] = u.title;
    onPick(picked, labels);
  };
  const c = cand.data;
  const close = () => !uploading && onClose();
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="dlg wide clpick" role="dialog" aria-modal="true" aria-labelledby="clpick-h" data-testid="cover-picker" onKeyDown={(e) => e.key === "Escape" && close()}>
        <div className="dh">
          <span id="clpick-h">{M.pickerTitle}</span>
          <Help label={`${M.pickerTitle}の説明`} text={t(M.pickerHelp)} testId="help-cover-picker" />
        </div>
        <div className="db">
          {c ? (
            <p className="clpick-to" data-testid="cover-picker-to">
              <span className="cllbl">{M.pickerTo}</span>
              <b>{c.recipient.partnerName}</b>
              {c.recipient.branchName ? <span>{c.recipient.branchName}</span> : null}
              {c.recipient.fax ? <span className="num">{`FAX ${c.recipient.fax}`}</span> : null}
            </p>
          ) : null}
          {fileOnly ? null : (
            <Segmented
              label={M.pickerTitle}
              value={tab}
              onChange={setTab}
              options={[
                { value: "docs", label: M.tabDocs, testId: "cover-picker-tab-docs" },
                { value: "received", label: M.tabReceived, testId: "cover-picker-tab-received" },
                { value: "file", label: M.tabFile, testId: "cover-picker-tab-file" },
              ]}
            />
          )}
          {cand.isError ? <ErrorText>{messageOf(cand.error)}</ErrorText> : null}
          {!fileOnly && !c && !cand.isError ? <p className="hint" role="status">{M.loading}</p> : null}
          {c && tab === "docs" ? (
            c.documents.length ? (
              <ul className="clpick-list" data-testid="cover-picker-docs">
                {c.documents.map((d) => {
                  const ref: DeliveryItemRef = { kind: d.kind, id: d.id } as DeliveryItemRef;
                  const k = coverItemKey(ref);
                  const on = has(k);
                  return (
                    <li key={k}>
                      <label className={"clpick-row" + (d.included ? " is-included" : "")}>
                        <input type="checkbox" checked={d.included || on} disabled={d.included || (!on && full)} onChange={() => toggle(ref)} data-testid="cover-picker-doc" data-key={k} />
                        <span className="nm">
                          <b>{`${d.label}${d.docNo ? ` ${d.docNo}` : ""}`}</b>
                          <span className="sub">{[d.subject, d.groupName ? `${t("{project}")}：${d.groupName}` : ""].filter(Boolean).join("　")}</span>
                        </span>
                        <span className="dt num">{d.included ? M.included : (d.date ?? "").replaceAll("-", "/")}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="hint" data-testid="cover-picker-empty">{M.pickerEmptyDocs}</p>
            )
          ) : null}
          {c && tab === "received" ? (
            c.groups.length ? (
              <>
                <label className="clpick-group">
                  <span className="cllbl">{t(M.pickerGroup)}</span>
                  <select className="inp" value={c.groupId ?? ""} onChange={(e) => setGroupId(e.target.value || null)} data-testid="cover-picker-group">
                    {c.groups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                </label>
                {c.received.length ? (
                  <ul className="clpick-list" data-testid="cover-picker-received">
                    {c.received.map((r) => {
                      const ref: DeliveryItemRef = { kind: "received", id: r.id };
                      const k = coverItemKey(ref);
                      const on = has(k);
                      return (
                        <li key={k}>
                          <label className={"clpick-row" + (r.included ? " is-included" : "")}>
                            <input type="checkbox" checked={r.included || on} disabled={r.included || (!on && full)} onChange={() => toggle(ref)} data-testid="cover-picker-rd" data-key={k} />
                            <span className="nm">
                              <b>{r.title}</b>
                              <span className="sub">{[r.kindLabel, r.format === "pdf" ? "PDF" : "画像", r.pages ? M.pagesShort.replace("{n}", String(r.pages)) : ""].filter(Boolean).join("　")}</span>
                            </span>
                            <span className="dt">{r.included ? M.included : ""}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="hint">{M.pickerEmptyReceived}</p>
                )}
              </>
            ) : (
              <p className="hint" data-testid="cover-picker-empty-groups">{t(M.pickerEmptyGroups)}</p>
            )
          ) : null}
          {tab === "file" ? (
            <>
              <FileDrop onFiles={(fs) => void upload(fs)} accept="application/pdf,.pdf" phoneAccept="application/pdf,.pdf" hint={t(M.pickerFileHelp)} chooseLabel={M.pickFile} disabled={uploading || full} testId="cover-picker-drop" inputTestId="cover-picker-file" />
              {uploading ? <p className="hint" role="status">{M.uploading}</p> : null}
              {uploads.length ? (
                <ul className="clpick-list" data-testid="cover-picker-uploads">
                  {uploads.map((u) => {
                    const ref: DeliveryItemRef = { kind: "file", id: u.id };
                    const k = coverItemKey(ref);
                    return (
                      <li key={k}>
                        <label className="clpick-row">
                          <input type="checkbox" checked={has(k)} disabled={!has(k) && full} onChange={() => toggle(ref)} data-testid="cover-picker-upload" />
                          <span className="nm">
                            <b>{u.title}</b>
                            <span className="sub">{`PDF　${M.pagesShort.replace("{n}", String(u.pages))}`}</span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              ) : null}
            </>
          ) : null}
          {full && room <= 0 ? <p className="err">{`⚠ ${M.bundleTooMany.replace("{n}", String(COVER_BUNDLE_LIMITS.items))}`}</p> : null}
          <ErrorText>{err}</ErrorText>
        </div>
        <div className="df">
          <button type="button" className="btn quiet" onClick={close} disabled={uploading} data-testid="cover-picker-cancel">
            取消
          </button>
          <span className="sp" />
          {picked.length ? <span className="hint" data-testid="cover-picker-count">{M.pickerSelected.replace("{n}", String(picked.length))}</span> : null}
          <button type="button" className="btn primary" disabled={!picked.length || uploading} onClick={done} data-testid="cover-picker-add">
            {M.pickerAdd}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * 同封書類1つずつの紙の画像（束ねの画面）。同じ書類は書類名を変えても取り直さない（鍵は書類の鍵だけ）。
 * 束ねで入るのと同じ PDF（受け取った書類の画像は A4 縦に収めたもの）を、見るだけの粗さ（1.5 倍＝約 108dpi）で描く
 */
export function useItemPages(items: ReadonlyArray<DeliveryItemRef>) {
  return useQueries({
    queries: items.map((ref) => {
      const key = coverItemKey(ref);
      return {
        queryKey: ["cover-item-pdf", key],
        queryFn: async (): Promise<PageImg[]> => {
          const blob = await fetchFile("/api/v1/cover-letters/item-pdf", { method: "POST", body: { item: { kind: ref.kind, id: ref.id, ...("form" in ref && ref.form ? { form: ref.form } : {}) } } });
          const got: PageImg[] = [];
          await renderPdf(await blob.arrayBuffer(), (p, i) => {
            got[i - 1] = p;
          }, { scale: 1.5 });
          return got;
        },
        staleTime: 5 * 60_000,
        retry: false,
      };
    }),
  });
}
